import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '../../../../../components/ui/Button';
import { Modal } from '../../../../../components/ui/Modal';
import { PhoneInput } from '../../../../../components/ui/PhoneInput';
import { composePhone, isCompletePhone, splitPhone } from '../../../../../utils/phone';
import { COUNTRY_ISO2 } from '../../../../../features/geo/geoConstants';
import {
  isValidEmail,
  formatTaxId,
  stripTaxId,
} from '../../../../../features/deliveries/orderUploadUtils';
import { DOCUMENT_LABEL_BY_COUNTRY } from '../../../../../features/deliveries/orderUploadConstants';
import { updateOrderRecipient } from '../../../../../features/orderDetails/orderDetailsThunks';

const inputClass = (hasError) =>
  `w-full bg-transparent border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-ninja-green transition-colors ${hasError ? 'border-danger' : 'border-border'
  }`;

const EditRecipientDialog = ({ onClose, order, onError }) => {
  const dispatch = useDispatch();
  const countryIso2 = order.seller?.company?.country_fk?.iso2 || COUNTRY_ISO2.CL;
  const documentLabel = DOCUMENT_LABEL_BY_COUNTRY[countryIso2] || DOCUMENT_LABEL_BY_COUNTRY.CL;

  // Read once, at mount: a refetch of the order while it is open must not wipe
  // what is being typed.
  const [form, setForm] = useState(() => {
    const { iso2, national } = splitPhone(order.contact_phone, countryIso2);
    return {
      name: order.recipient || '',
      email: order.contact_email || '',
      phoneCountry: iso2,
      phone: national,
      taxId: formatTaxId(order.contact_rut, countryIso2),
    };
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const found = {};
    if (!form.name.trim()) found.name = 'Campo obligatorio';
    if (!form.email.trim()) {
      found.email = 'Campo obligatorio';
    } else if (!isValidEmail(form.email)) {
      found.email = 'Formato incorrecto';
    }
    if (!form.phone) {
      found.phone = 'Campo obligatorio';
    } else if (!isCompletePhone(form.phone, form.phoneCountry)) {
      found.phone = 'Número incompleto';
    }
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await dispatch(
        updateOrderRecipient({
          orderId: order.id,
          values: {
            name: form.name,
            email: form.email,
            phone: composePhone(form.phoneCountry, form.phone),
            taxId: stripTaxId(form.taxId),
          },
        })
      ).unwrap();
      onClose();
    } catch (error) {
      onError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Editar Destinatario" testId="order-edit-recipient-modal" busy={saving}>
      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1.5">
            Nombre
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            className={inputClass(errors.name)}
          />
          {errors.name && <p className="text-danger text-xs mt-1">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1.5">
            Mail
          </label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setField('email', e.target.value)}
            className={inputClass(errors.email)}
          />
          {errors.email && <p className="text-danger text-xs mt-1">{errors.email}</p>}
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1.5">
            Teléfono
          </label>
          <PhoneInput
            countryIso2={form.phoneCountry}
            onCountryChange={(value) => setField('phoneCountry', value)}
            value={form.phone}
            onChange={(value) => setField('phone', value)}
            hasError={Boolean(errors.phone)}
          />
          {errors.phone && <p className="text-danger text-xs mt-1">{errors.phone}</p>}
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1.5">
            {documentLabel} <span className="normal-case font-medium">(opcional)</span>
          </label>
          <input
            type="text"
            value={form.taxId}
            onChange={(e) => setField('taxId', formatTaxId(e.target.value, countryIso2))}
            className={inputClass(false)}
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 mt-6">
        <Button variant="subtle" pill onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          pill
          onClick={handleSave}
          loading={saving}
          loadingLabel="Guardando..."
        >
          Guardar
        </Button>
      </div>
    </Modal>
  );
};

/**
 * Editor for the contact block of an order.
 *
 * The phone is edited as the same composite field the upload form uses — a
 * country selector plus a masked local number — and travels back in E.164.
 * Stored numbers are split into those two halves on open, so an order created
 * before that format existed can still be edited without retyping.
 *
 * The tax id follows the country, RUT or RFC, the way the manual form does. It
 * is shown masked and stored plain, and it is optional: an order can travel
 * without one, and refusing to save the rest of the contact over a missing RUT
 * would be worse than the gap.
 *
 * Mounted only while open: each opening starts from the order as it is then,
 * and nothing that arrives while it is open overwrites the form.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object} props.order
 * @param {(message: string) => void} props.onError
 */
const EditRecipientModal = (props) => (props.isOpen ? <EditRecipientDialog {...props} /> : null);

export default EditRecipientModal;
